import React, { useState, useEffect } from "react";
import { ReactComponent as AddUserIcon } from "../../../images/add-user.svg";
import { ReactComponent as AlertIcon } from "../../../images/alert-circle.svg";
import "./addUser.scss";
import Modal, { handleCheckboxKeyDown } from "../../../common/modal/modal.tsx";
import UserSearchDropdown, {
  UserSearchDropdownItem,
} from "../../../common/dropdown/userSearchDropdown.tsx";
import {
  searchUserByName,
  addUser,
  editUser,
  loadUsers,
  loadGroups,
  loadRoles,
} from "../userSlice.ts";
import { UserItem } from "../userService.ts";
import SuccessfullToast from "../../../common/toast/successfulToast.tsx";
import ChangePassword from "../password/changePassword.tsx";
import GroupDropdown from "./userRoot/groupDropdown.tsx";
import Dropdown, { DropdownItems } from "../../../common/dropdown/dropdown.tsx";
import Tooltip from "../../../common/tooltip/tooltip.tsx";
import ConfirmationDialog from "../../../common/confirmationDialog/confirmationDialog.tsx";
import Loader from "../../../common/loader/loader.tsx";
import AddCustomer from "../../customerDetails/addCustomerDetails/addCustomerDetails.tsx";
import {
  addCustomer,
  loadCustomer,
} from "../../customerDetails/customerSlice.ts";
import { CustomerItem } from "../../customerDetails/customerService.ts";
import { useFormChangeDetection } from "../../../common/formChangeDetection/formChangeDetection.tsx";
import useConfig from "../../../hooks/useConfig.ts";
import { useAppDispatch, useAppSelector } from "../../../hooks/storeHooks.ts";
import useUserAccess from "../../../hooks/useUserAcess.ts";
import { fetchLegacyUsersAsync, LegacyUser } from "../userService.ts";

interface AddUserProps {
  addUser: (newUser: UserItem) => void;
  editUser: (updatedUser: UserItem) => void;
  userToEdit?: UserItem | null;
}

interface PumpCheckerRoles {
  id: number;
  name: string;
  value: string;
}

const pumpCheckerRoles = [
  { id: 1, name: "SYS - System Admin", value: "SYS" },
  { id: 2, name: "ADM - User Admin", value: "ADM" },
  { id: 3, name: "MAN - Manufacturer", value: "MAN" },
  { id: 4, name: "FOR - Foreman", value: "FOR" },
  { id: 5, name: "INS - Installer", value: "INS" },
  { id: 6, name: "USR - Basic User", value: "USR" },
];

const AddUser: React.FC<AddUserProps> = ({ userToEdit }) => {
  const dispatch = useAppDispatch();
  const searchResults = useAppSelector((state) => state.users.searchResults);
  const userSearchLoading = useAppSelector(
    (state) => state.users.userSearchLoading
  );
  const groups = useAppSelector((state) => state.users.groups);
  const roles = useAppSelector((state) => state.users.roles);
  const users = useAppSelector((state) => state.users.users);
  const userSaveLoading = useAppSelector(
    (state) => state.users.userSaveLoading
  );
  const customerNames = useAppSelector((state) => state.customers.customer);
  const { isCloud, isOPIC } = useConfig();
  const { isAdmin } = useUserAccess();

  const customerItems = customerNames
    .map((customer) => ({
      id: customer.id,
      name: customer.name || "",
    }))
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const initialFormData = {
    id: "",
    createdBy: "",
    createdOnDate: "",
    lastModifiedBy: "",
    lastModifiedDate: "",
    UserId: "",
    cnX_UserId: null,
    customerName: isCloud
      ? ""
      : customerNames.length > 0
        ? customerNames[0].name
        : "",
    customerId: isCloud
      ? null
      : customerNames.length > 0
        ? customerNames[0].id
        : null,
    isEnabled: true,
    is2FAEnabled: false,
    userType: "",
    userName: "",
    password: "",
    contactId: "",
    lastLogin: "",
    grafaEditor: "",
    license: {
      licenseType: "",
      expirationDate: "",
    },
    b2cdomainname: "",
    mostCommonPreferences: "",
    mustChangePassword: true,
    isSSO: false,
    userPreference: {
      id: "",
      createdBy: "",
      createdOnDate: "",
      lastModifiedBy: "",
      lastModifiedDate: "",
      userId: "",
      preferenceItem: {
        groupName: "",
        propertyItem: [],
      },
    },
    userRoles: {
      id: "",
      createdBy: "",
      createdOnDate: "",
      lastModifiedBy: null,
      lastModifiedDate: "",
      roleIds: [],
      userId: "",
    },
    pumpChecker: {
      firstName: "",
      lastName: "",
      isEnabled: true,
      role: "",
      email: "",
    },
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<UserItem>(initialFormData);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [UserSearchDropdownItems, setUserSearchDropdownItems] = useState<
    UserSearchDropdownItem[]
  >([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] =
    useState(false);
  const [textcolor, setTextColor] = useState("");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [previousValue, setPreviousValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const { setOriginalFormData, hasChanges } =
    useFormChangeDetection<CustomerItem>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [legacyUsers, setLegacyUsers] = useState<LegacyUser[]>([]);
  const [selectedLegacyUser, setSelectedLegacyUser] = useState<string>("");
  const [legacySearchValue, setLegacySearchValue] = useState("");
  const [legacyLoading, setLegacyLoading] = useState(false);

  const isChampionXEmail = formData.userName
  ?.toLowerCase()
  .endsWith("@championx.com");

  useEffect(() => {
    if (!dataLoaded) {
      if (customerNames.length === 0) {
        dispatch(loadCustomer());
      }
      if (groups.length === 0) {
        dispatch(loadGroups());
      }
      if (roles.length === 0) {
        dispatch(loadRoles());
      }
      setDataLoaded(true);
    }
  }, [dispatch, dataLoaded, customerNames.length, groups.length, roles.length]);

  useEffect(() => {
    if (userToEdit) {
      setFormData({
        ...userToEdit,
        id: userToEdit.id.toString(),
        license: userToEdit.license || [
          {
            licenseType: "",
            expirationDate: "",
          },
        ],
        pumpChecker: {
          firstName: userToEdit.firstName || "",
          lastName: userToEdit.lastName || "",
          isEnabled: userToEdit.isEnabled || false,
          role: userToEdit.pumpCheckerRole || "",
          email: userToEdit.userName || "",
        },
        ...(!isCloud && {
          customerId: userToEdit?.customerId ?? customerItems[0]?.id,
          customerName: userToEdit?.customerName || customerItems[0]?.name,
        }),
      });
      setOriginalFormData({ ...userToEdit });
      setIsEditMode(true);
      openModal();
    }
  }, [userToEdit, setOriginalFormData]);

  const shouldShowPasswordFields = false;
  /*
    Earlier for Cloud: Password is required if not SSO. 
    For OPIC: Password is always required. 
    For non-Cloud non-OPIC: Password is required if not SSO.
    
    We changed this logic so that for both Cloud and non-Cloud, password is not required if SSO is enabled. 
    This is because even for OPIC, if SSO is enabled, password is not required.
   */
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const [initialIsSSO, setInitialIsSSO] = useState<boolean | null>(null);

  useEffect(() => {
    if (isEditMode) {
      setInitialIsSSO(formData.isSSO);
      setIsPasswordRequired(false);
    }
  }, [isEditMode]);

  useEffect(() => {
    if (userToEdit?.linkedXSPOCUser) {
      setSelectedLegacyUser(userToEdit.linkedXSPOCUser);
      setLegacySearchValue(userToEdit.linkedXSPOCUser);
    }
  }, [userToEdit]);

  useEffect(() => {
    if (isModalOpen) {
      loadAllLegacyUsers();
    }
  }, [isModalOpen]);

  useEffect(() => {
    let isValid = true;
    const newErrors: { [key: string]: string } = {};

    if (!isCloud) {
      isValid = formData.pumpChecker.role.trim() !== "" && isValid;
    }
    isValid =
      formData.userName.trim() !== "" &&
      formData.customerName.trim() !== "" &&
      formData.pumpChecker.firstName.trim() !== "" &&
      formData.pumpChecker.lastName.trim() !== "" &&
      formData.userPreference !== null &&
      formData.userPreference?.preferenceItem?.groupName.trim() !== "" &&
      isValid;

    if (isCloud && formData.isSSO && !formData.b2cdomainname) {
      newErrors.b2cdomainname =
        "B2CDomain Name is required when isSSO is enabled";
    }

    if (
      (isEditMode &&
        !(
          !isPasswordRequired ||
          (initialIsSSO === false && formData.password)
        )) ||
      !isEditMode
    ) {
      if (shouldShowPasswordFields) {
        isValid =
          isValid &&
          formData.password?.trim() !== "" &&
          confirmPassword?.trim() !== "";

        if (formData.password && formData.password.length < 5) {
          newErrors.password =
            "Password needs to be at least 5 characters long";
        }

        if (confirmPassword && formData.password !== confirmPassword) {
          newErrors.confirmPassword = "Passwords do not match";
        }
      }
    }

    if (!isEditMode) {
      const emailPattern =
        /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;

      if (formData.userName) {
        const userNameExists = users.some(
          (user) =>
            user.userName &&
            user.userName.toLowerCase() === formData.userName.toLowerCase()
        );

        if (userNameExists) {
          newErrors.userName = isCloud
            ? "User name already exists"
            : "Email address already exists";
        } else if (!isCloud && !emailPattern.test(formData.userName)) {
          newErrors.userName = "Please enter a valid email address";
        }
      }
    }

    setErrors(newErrors);
    setIsFormValid(isValid && Object.keys(newErrors).length === 0);
  }, [formData, confirmPassword, users, isEditMode, isOPIC, isCloud]);

  useEffect(() => {
    setInputValue(formData.userName);
  }, [formData.userName]);

  useEffect(() => {
    if (inputValue.length > 2) {
      const filteredItems = searchResults.filter((result) =>
        result.email.toLowerCase().includes(inputValue.toLowerCase())
      );
      setUserSearchDropdownItems(
        filteredItems.map((result, index) => ({
          id: result.id,
          name: result.email,
          displayName: result.email,
          isSSO: result.sso,
          key: `${result.id}-${index}`,
          firstName: result.firstName,
          lastName: result.lastName,
        }))
      );
    }
  }, [searchResults, inputValue]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(initialFormData);
    setConfirmPassword("");
    setErrors({});
    setIsEditMode(false);
    setUserSearchDropdownItems([]);
    setInputValue("");
    setLegacyUsers([]);
    setSelectedLegacyUser("");
    setLegacySearchValue("");
  };

  const openAddUserModal = () => {
    setIsEditMode(false);
    setOriginalFormData(initialFormData);
    setFormData(initialFormData);
    openModal();
    setUserSearchDropdownItems([]);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevState) => {
      let updatedFormData = { ...prevState };

      if (name.startsWith("pumpChecker.")) {
        const pumpCheckerField = name.split(".")[1];
        updatedFormData = {
          ...prevState,
          pumpChecker: {
            ...prevState.pumpChecker,
            [pumpCheckerField]:
              type === "checkbox" ? checked : value.replace(/^\s+/, ""),
          },
        };
      } else {
        if (name === "userName") {
          const trimmedValue = value.trim();
          const isChampionX = trimmedValue.toLowerCase().endsWith("@championx.com");
          
          updatedFormData[name] = trimmedValue;
          updatedFormData.isSSO = isChampionX;
          
          if (isChampionX) {
            setIsPasswordRequired(false);
            updatedFormData.is2FAEnabled = false;
            updatedFormData.password = "";
            setConfirmPassword("");
          }
        } else if (name === "password") {
          updatedFormData[name] = value.trim();
          if (isEditMode && initialIsSSO === false && value) {
            setIsPasswordRequired(false);
          }
        } else {
          updatedFormData[name] =
            type === "checkbox" ? checked : value.replace(/^\s+/, ""); // Handle other fields
        }
      }

      if (name === "userName") {
        updatedFormData.pumpChecker.email = value.trim();
      }

      if (name === "isEnabled") {
        updatedFormData.pumpChecker.isEnabled = checked;
      }
      if (name === "is2FAEnabled") {
        updatedFormData.is2FAEnabled = checked;
      }

      if (name === "isSSO") {
        if (!isOPIC) {
          if (initialIsSSO && !checked) {
            setIsPasswordRequired(true);
          }
        }
        updatedFormData.b2cdomainname = checked
          ? ""
          : updatedFormData.b2cdomainname;
        if (checked) {
          setIsPasswordRequired(false);
          updatedFormData.is2FAEnabled = false;
          updatedFormData.password = "";
          setConfirmPassword("");
        } else if (formData.password) {
          setIsPasswordRequired(false);
        }
      }

      return updatedFormData;
    });
  };

  const handleUserDropdownChange = (selectedUser: UserSearchDropdownItem) => {
    setFormData((prevState) => ({
      ...prevState,
      cnX_UserId: String(selectedUser.id),
      userName: selectedUser.name,
      isSSO: selectedUser.sso ?? false,
      isEnabled: true,
      pumpChecker: {
        ...prevState.pumpChecker,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
      },
    }));
    setInputValue(selectedUser.name);
  };

  const handleDropdownChange = (
    item: PumpCheckerRoles | DropdownItems,
    field: string
  ) => {
    setFormData((prevState) => {
      const updatedFormData = { ...prevState };

      if (field.startsWith("pumpChecker.")) {
        const pumpCheckerField = field.split(".")[1];
        updatedFormData.pumpChecker = {
          ...prevState.pumpChecker,
          [pumpCheckerField]: item.value,
        };
      } else {
        updatedFormData[field] = item.name;
      }

      if (field === "customerName") {
        updatedFormData.customerId = item.id;
      }

      return updatedFormData;
    });
  };

  const addCustomerHandler = (newCustomer: CustomerItem) => {
    dispatch(addCustomer(newCustomer));
  };

  const customNoResultsMessage = (
    <>
      <p className="no-customer-found">Customer not listed?</p>
      <span className="add-new-customer">
        <AddCustomer addCustomer={addCustomerHandler} triggerType="button" />
      </span>
    </>
  );

  const handleUserSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const currentDate = new Date().toISOString();

    const updatedFormData: UserItem = {
      ...formData,
      cnX_UserId: formData.cnX_UserId ?? null,
      createdOnDate: formData.createdOnDate
        ? formData.createdOnDate
        : currentDate,
      lastModifiedDate: currentDate,
      lastLogin: formData.lastLogin || currentDate,
      license: {
        licenseType: formData.license.licenseType || "",
        expirationDate: formData.license.expirationDate || currentDate,
      },
      password: isEditMode
        ? !isOPIC && !formData.isSSO
          ? formData.password
          : undefined
        : formData.password,
      userPreference: {
        ...formData.userPreference,
        id: formData.userPreference?.id,
        createdOnDate: "2024-06-13T22:43:20.594Z",
        lastModifiedDate: "2024-06-13T22:43:20.594Z",
        userId: formData.id,
      },
      userRoles: {
        ...formData.userRoles,
        id: formData.userRoles?.id ?? "",
        createdOnDate: "2024-06-13T22:42:44.306Z",
        lastModifiedDate: "2024-06-13T22:42:44.306Z",
        userId: formData.id ?? "",
      },
      action: isEditMode ? "update" : "insert",

      ...(isCloud && {
        b2cdomainname: formData.isSSO ? formData.b2cdomainname : "",
      }),

      ...(!isCloud && {
        isSSO: isOPIC ? true : formData.isSSO,
        pumpChecker: {
          ...formData.pumpChecker,
          email: formData.pumpChecker.email || formData.userName.trim(),
        },
        linkedXSPOCUser: selectedLegacyUser || null,

        // Exclude top-level duplicates
        firstName: undefined,
        lastName: undefined,
        pumpCheckerRole: undefined,
      }),
    };

    if (Object.keys(errors).length === 0) {
      try {
        const action = isEditMode
          ? editUser({
            userId: formData.id,
            updatedUser: updatedFormData,
          })
          : addUser(updatedFormData);
        
        
        const response = await dispatch(action as any).unwrap();

        if (response?.status === "true" || response?.status === "success") {
          setSaveFailed(false);
          setShowToast(true);
          setToastMessage(response.message);
          setTextColor("green");
          dispatch(loadUsers());
          closeModal();
        } else {
          setSaveFailed(true);
          setToastMessage(
            `Failed to ${isEditMode ? "update" : "add"} user: ${response?.message
            }`
          );
          setTextColor("red");
          setShowToast(true);
        }
      } catch (error) {
        setSaveFailed(true);
        setToastMessage(error || "An unexpected error occurred.");
        setTextColor("red");
        setShowToast(true);
        dispatch(loadUsers());
        closeModal();
      }
    }
  };

  const handleInputChangeLogging = async (value: string) => {
    const trimmedValue = value.replace(/^\s+/, "");
    setInputValue(trimmedValue);
    setFormData((prevState) => ({
      ...prevState,
      userName: trimmedValue,
    }));

    if (trimmedValue.length === 3) {
      if (trimmedValue !== previousValue) {
        dispatch(searchUserByName(trimmedValue));
        setPreviousValue(trimmedValue);
      }
    } else if (trimmedValue.length < 3) {
      setUserSearchDropdownItems([]);
      setPreviousValue("");
    }
  };

  const openPasswordModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsPasswordModalOpen(true);
  };

  const closePasswordModal = () => {
    setIsPasswordModalOpen(false);
  };

  const handleUserRootChange = (selectedRoot: DropdownItems) => {
    setFormData((prevState) => ({
      ...prevState,
      userPreference: {
        ...prevState.userPreference,
        preferenceItem: {
          ...prevState.userPreference?.preferenceItem,
          groupName: selectedRoot.name,
        },
      },
    }));
  };

  const handleCancelClick = () => {
    if (saveFailed) {
      dispatch(loadUsers());
      setSaveFailed(false);
    }
    if (hasChanges(formData)) {
      setIsConfirmationDialogOpen(true);
    } else {
      handleConfirmCancel();
    }
  };

  const handleConfirmationDialogClose = () =>
    setIsConfirmationDialogOpen(false);

  const handleConfirmCancel = () => {
    setIsConfirmationDialogOpen(false);
    closeModal();
  };

  const handleLegacySearch = async (value: string) => {
    const trimmedValue = value.trim();
    setLegacySearchValue(trimmedValue);

    if (!trimmedValue) {
      setSelectedLegacyUser("");
      setFormData((prev) => ({
        ...prev,
        linkedXSPOCUser: null,
      }));
      setLegacyUsers([]);
      return;
    }
    
    setLegacyLoading(true);
    try {
      const data = await fetchLegacyUsersAsync(trimmedValue);
      setLegacyUsers(data);
    } finally {
      setLegacyLoading(false);
    }
  };

  const handleLegacySelect = (user: any) => {
    if (user.isAlreadyLinked) {
      setToastMessage(
        `Legacy user is already linked with ${user.linkedUserName}`
      );
      setTextColor("red");
      setShowToast(true);
      return;
    }

    setSelectedLegacyUser(user.name);
    setLegacySearchValue(user.name);

    setFormData((prev) => ({
      ...prev,
      linkedXSPOCUser: user.name,
    }));
  };

  const loadAllLegacyUsers = async () => {
    setLegacyLoading(true);
    try {
      const data = await fetchLegacyUsersAsync("");
      setLegacyUsers(data);
    } catch (err) {
      console.error("Error loading legacy users", err);
    } finally {
      setLegacyLoading(false);
    }
  };

  const legacyDropdownItems = legacyUsers.map((user, index) => ({
    id: index.toString(),
    name: user.xspocUser,
    displayName: user.xspocUser,
    key: `${user.xspocUser}-${index}`,
    isAlreadyLinked: user.isAlreadyLinked,
    linkedUserName: user.linkedUser?.linkedUserName,
  }));

  return (
    <div>
      {isAdmin && (
        <Tooltip content="Add new user" direction="top">
          <span className="add-user-icon" onClick={openAddUserModal}>
            <AddUserIcon />
          </span>
        </Tooltip>
      )}

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div style={{ position: "relative" }}>
          {userSaveLoading && (
            <div className="spinner-overlay">
              <Loader isOverlay={true} />
            </div>
          )}
          <form
            className="add-user-form"
            onSubmit={handleUserSubmit}
            onKeyDown={(e) => handleCheckboxKeyDown(e)}
          >
            <div className="form-row" style={{ marginBottom: "10px" }}>
              <label style={{ fontSize: "20px" }}>
                {isEditMode ? "Edit User" : "Add New User"}
              </label>
            </div>
            <div className="form-row">
              <div className="form-group form-group-inline">
                <input
                  type="checkbox"
                  id="isSSO"
                  name="isSSO"
                  checked={
                    isEditMode || isCloud || !isOPIC ? formData.isSSO : true
                  }
                  disabled={(!isCloud && isOPIC) || isChampionXEmail}
                  onChange={handleInputChange}
                  style={{ width: "15px" }}
                  className="settings-checkbox-input"
                />
                <label htmlFor="isSSO">SSO</label>
              </div>
              <div className="form-group form-group-inline">
                <input
                  type="checkbox"
                  id="isEnabled"
                  name="isEnabled"
                  checked={formData.isEnabled}
                  onChange={handleInputChange}
                  style={{ width: "15px" }}
                  className="settings-checkbox-input"
                />
                <label htmlFor="isEnabled">Is Enabled</label>
              </div>
            </div>
            {!formData.isSSO && (
              <div className="form-row">
                <div >
                  <input
                    type="checkbox"
                    id="is2FAEnabled"
                    name="is2FAEnabled"
                  checked={formData.is2FAEnabled}
                  onChange={handleInputChange}
                  className="checkbox-input"
                  disabled={formData.isSSO}
                />
                <label htmlFor="is2FAEnabled" className='va-tb'>
                  Enable 2FA
                </label>
              </div>
            </div>
            )}
            <div className="form-row">
              <div className="form-group dropdown-form-group">
                <label>
                  <span>Customer Name:</span>
                  <span className="required-asterisk">*</span>
                </label>
                <Dropdown
                  items={customerItems}
                  defaultOption={
                    isCloud
                      ? formData.customerName || "Select customer"
                      : customerItems.length > 0
                        ? customerItems[0].name
                        : "Select customer"
                  }
                  onChange={(item) =>
                    handleDropdownChange(item, "customerName")
                  }
                  disabled={isCloud ? isEditMode : true}
                  noResultsMessage={isCloud ? customNoResultsMessage : <></>}
                />
              </div>
              <div className="form-group">
                <label>
                  <span>{isCloud ? "Username:" : "Email Address:"}</span>
                  <span className="required-asterisk">*</span>
                </label>
                <div className="alert-icon-container">
                  {isCloud ? (
                    <UserSearchDropdown
                      items={UserSearchDropdownItems}
                      defaultOption={formData.userName}
                      onChange={handleUserDropdownChange}
                      onInputChange={handleInputChangeLogging}
                      inputValue={inputValue}
                      disabled={isEditMode}
                      isLoading={userSearchLoading}
                      maxLength={200}
                    />
                  ) : (
                    <input
                      type="text"
                      name="userName"
                      value={formData.userName}
                      onChange={handleInputChange}
                      disabled={isEditMode}
                      autoComplete="off"
                      maxLength={200}
                    />
                  )}
                  {errors.userName && (
                    <span className="error-icon">
                      <Tooltip content={errors.userName} direction="top">
                        <AlertIcon />
                      </Tooltip>
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <span>First Name:</span>
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  name="pumpChecker.firstName"
                  value={formData.pumpChecker.firstName}
                  onChange={handleInputChange}
                  autoComplete="off"
                  maxLength={300}
                />
              </div>
              <div className="form-group">
                <label>
                  <span>Last Name:</span>
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="text"
                  name="pumpChecker.lastName"
                  value={formData.pumpChecker.lastName}
                  onChange={handleInputChange}
                  autoComplete="off"
                  maxLength={300}
                />
              </div>
            </div>
            <div className="form-row">
              <div className={`form-group ${isCloud ? "user-root-field" : ""}`}>
                <label>
                  <span>User Root:</span>
                  <span className="required-asterisk">*</span>
                </label>
                <GroupDropdown
                  items={groups}
                  defaultOption={
                    formData.userPreference?.preferenceItem?.groupName ||
                    "Select a root"
                  }
                  onChange={handleUserRootChange}
                  inputValue={
                    formData.userPreference?.preferenceItem?.groupName || ""
                  }
                  className="cloud-dropdown-width"
                />
              </div>
              {!isCloud && (
                <div className="form-group dropdown-form-group">
                  <label>
                    <span>Pump Checker Role:</span>
                    <span className="required-asterisk">*</span>
                  </label>
                  <Dropdown
                    items={pumpCheckerRoles}
                    defaultOption={formData.pumpCheckerRole || "Select role"}
                    onChange={(item) =>
                      handleDropdownChange(item, "pumpChecker.role")
                    }
                  />
                </div>
              )}
            </div>
            {(
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <span>Link Legacy User (XSPOC):</span>
                  </label>

                  <UserSearchDropdown
                    items={legacyDropdownItems}
                    defaultOption="Search legacy user..." 
                    onChange={handleLegacySelect}
                    onInputChange={handleLegacySearch}
                    inputValue={legacySearchValue}
                    isLoading={legacyLoading}
                    maxLength={200}
                  />
                </div>
              </div>
            )}
            {shouldShowPasswordFields && (
              <div className="form-row">
                {isEditMode &&
                  (!isPasswordRequired ||
                    (initialIsSSO === false && formData.password)) ? (
                  <span className="form-change-password-button">
                    <div className="form-group">
                      <button
                        className="change-password-button"
                        onClick={openPasswordModal}
                      >
                        Change Password
                      </button>
                      <ChangePassword
                        isOpen={isPasswordModalOpen}
                        onClose={closePasswordModal}
                        id={formData.id}
                        userName={formData.userName}
                        cnX_UserId={formData.cnX_UserId || null}
                      />
                      <div className="form-group-password-check">
                        <input
                          type="checkbox"
                          id="changePassword"
                          name="mustChangePassword"
                          checked={formData.mustChangePassword}
                          onChange={handleInputChange}
                          className="checkbox-input"
                          disabled={
                            formData.isSSO ||
                            formData.b2cdomainname?.trim() !== ""
                          }
                        />
                        <label htmlFor="changePassword">
                          User must change password at first login
                        </label>
                      </div>
                    </div>
                  </span>
                ) : (
                  <>
                    <div className="form-group">
                      <label>
                        <span>Password:</span>
                        <span className="required-asterisk">*</span>
                      </label>
                      <div className="alert-icon-container">
                        <input
                          type="password"
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          maxLength={100}
                        />
                        {errors.password && (
                          <span className="error-icon">
                            <Tooltip content={errors.password} direction="top">
                              <AlertIcon />
                            </Tooltip>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label>
                        <span>Confirm Password:</span>
                        <span className="required-asterisk">*</span>
                      </label>
                      <div className="alert-icon-container">
                        <input
                          type="password"
                          name="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          maxLength={100}
                        />
                        {errors.confirmPassword && (
                          <span className="error-icon">
                            <Tooltip
                              content={errors.confirmPassword}
                              direction="top"
                            >
                              <AlertIcon />
                            </Tooltip>
                          </span>
                        )}
                      </div>
                      <div className="form-group-password-check">
                        <input
                          type="checkbox"
                          id="changePassword"
                          name="mustChangePassword"
                          checked={formData.mustChangePassword}
                          onChange={handleInputChange}
                          className="checkbox-input"
                          disabled={formData.isSSO}
                        />
                        <label htmlFor="changePassword">
                          User must change password at first login
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {isCloud && formData.isSSO && (
              <div className="form-row">
                <div className="form-group">
                  <label>
                    <span>B2C Domain:</span>
                    {formData.isSSO && (
                      <span className="required-asterisk">*</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="b2cdomainname"
                    value={formData.b2cdomainname || ""}
                    onChange={handleInputChange}
                    autoComplete="off"
                  />
                </div>
                <div className="form-group"></div>
              </div>
            )}
            <div
              className="form-row"
              style={{
                marginTop: shouldShowPasswordFields ? undefined : "4px",
              }}
            >
              <label>User Security Settings:</label>
            </div>
            <div className="form-row" style={{ marginTop: "6px" }}>
              <span className="form-user-roles">
                {Array.isArray(roles) && roles.length > 0 ? (
                  roles.map((role, index) => (
                    <div className="settings-form-group" key={role.id}>
                      <input
                        type="checkbox"
                        id={`roles-${role.id}`}
                        name={`roles.${role.id}`}
                        checked={
                          formData.userRoles?.roleIds?.includes(role.id) ||
                          false
                        }
                        onChange={(e) => {
                          const roleId = role.id;
                          setFormData((prevState) => {
                            const checked = e.target.checked;
                            let updatedRoles = [
                              ...(prevState.userRoles?.roleIds || []),
                            ];

                            if (checked) {
                              updatedRoles.push(roleId);
                            } else {
                              updatedRoles = updatedRoles.filter(
                                (id) => id !== roleId
                              );
                            }

                            return {
                              ...prevState,
                              userRoles: {
                                ...prevState.userRoles,
                                roleIds: updatedRoles,
                              },
                            };
                          });
                        }}
                        className="settings-checkbox-input"
                      />
                      <label
                        htmlFor={`roles-${role.id}`}
                        className="settings-label"
                      >
                        {role.roleName}
                      </label>
                    </div>
                  ))
                ) : (
                  <></>
                )}
              </span>
            </div>
            <div className="form-mandatory-message">
              <p className="mandatory-fields-message">
                Fields marked with<span className="required-asterisk">*</span>{" "}
                are mandatory.
              </p>
            </div>
            <div
              className="form-row"
              style={{ gap: "10px", marginTop: "-15px" }}
            >
              <button
                type="submit"
                className="add-user-button"
                disabled={
                  isEditMode
                    ? !isFormValid || !hasChanges(formData)
                    : !isFormValid
                }
              >
                {isEditMode ? "Save" : "Add User"}
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={handleCancelClick}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </Modal>
      {showToast && (
        <SuccessfullToast
          message={toastMessage}
          isShowEnabled={showToast}
          setShow={setShowToast}
          colorText={textcolor}
        />
      )}
      <ConfirmationDialog
        isOpen={isConfirmationDialogOpen}
        onClose={handleConfirmationDialogClose}
        onConfirm={handleConfirmCancel}
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
      />
    </div>
  );
};

export default AddUser;
